import React, { useState, useCallback } from "react";
import { Play, ExternalLink } from "lucide-react";

/**
 * Instagram reel — FACADE pattern.
 *
 * On load we render only a poster image + a play affordance. Instagram's
 * embed.js is fetched ONLY on click, once, and shared across every embed on
 * the page (module-level singleton) — three iframes at load would wreck LCP.
 *
 * A real <a href> to the reel is always present in the markup (the caption
 * link), so the prerendered HTML has a working link even if embed.js never
 * loads or the reel is later deleted. The 9/16 frame is reserved up front so
 * swapping in the iframe never shifts layout.
 */

// Shared, one-time loader for Instagram's embed script.
let igScriptPromise = null;
function loadInstagramEmbed() {
  if (igScriptPromise) return igScriptPromise;
  igScriptPromise = new Promise((resolve) => {
    if (typeof window !== "undefined" && window.instgrm) return resolve();
    const s = document.createElement("script");
    s.src = "https://www.instagram.com/embed.js";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => resolve(); // fail soft — the caption <a> still works
    document.body.appendChild(s);
  });
  return igScriptPromise;
}

export default function ReelEmbed({ url, poster, alt, posterSize }) {
  const [active, setActive] = useState(false);

  const activate = useCallback(async () => {
    setActive(true);
    await loadInstagramEmbed();
    if (window.instgrm && window.instgrm.Embeds) {
      // Let the blockquote mount first, then process it into an iframe.
      requestAnimationFrame(() => window.instgrm.Embeds.process());
    }
  }, []);

  return (
    <figure className="reel" data-testid="reel-embed">
      <div className="reel-frame">
        {!active ? (
          <button type="button" className="reel-facade" onClick={activate} aria-label={`Play Instagram reel — ${alt}`}>
            <img src={poster} alt={alt} width={posterSize ? posterSize[0] : undefined} height={posterSize ? posterSize[1] : undefined} loading="lazy" decoding="async" className="reel-poster" />
            <span className="reel-play" aria-hidden="true"><Play size={22} strokeWidth={2} fill="currentColor" /></span>
          </button>
        ) : (
          <blockquote
            className="instagram-media"
            data-instgrm-permalink={url}
            data-instgrm-version="14"
            style={{ background: "#0F0A07", border: 0, margin: 0, padding: 0, width: "100%", minWidth: 0 }}
          >
            <a href={url} target="_blank" rel="noopener noreferrer">View this reel on Instagram</a>
          </blockquote>
        )}
      </div>
      <figcaption className="reel-cap">
        <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5">
          Watch on Instagram <ExternalLink size={13} />
        </a>
      </figcaption>
    </figure>
  );
}
